import moment from 'moment';
import Api from "services/Api";
import store from "store/index";
import Validator from 'services/Validator';
import { requestPayload } from 'store/modules/claim';
import { personTemplate } from 'store/modules/shared/templates';
import { dateFromServer } from 'services/helper';
import { isStepValid} from 'services/routingMethods';
import { calcAgeDeath, isValidIncome, unthrottledIncome, financeValues } from 'services/personFunctions';
import { previousValid } from 'store/generators/step';

export const mutationTypes = {
    RESET: 'RESET',
    LOAD_FROM_SERVER: 'LOAD_FROM_SERVER',
    UPDATE_DISCOUNT_RATE: 'UPDATE_DISCOUNT_RATE',
    UPDATE_DISCOUNT_TABLE: 'UPDATE_DISCOUNT_TABLE',
    RESET_MULTIPLIER_TABLE: 'RESET_MULTIPLIER_TABLE',
    UPDATE_MULTIPLIER_VALUE: 'UPDATE_MULTIPLIER_VALUE',
    LOAD_MULTIPLIER_TABLE1AND2: 'LOAD_MULTIPLIER_TABLE1AND2',
    UPDATE_MULTIPLIER_TABLE28_ROW: 'UPDATE_MULTIPLIER_TABLE28_ROW',
};

/**
 * Format data to table rows
 *
 * @param row
 */
const convertTableRowFromServer = row => ({
    period: row.period,
    numberOfDependants: row.numberOfDependants,
    start: moment(row.start, 'DD-MM-YYYY').format('DD/MM/YYYY'),
    end: moment(row.end, 'DD-MM-YYYY').format('DD/MM/YYYY'),
    duration: row.duration,
    formula: {
        base: row.formula.base,
        deceasedNet: row.formula.deceased_net,
        dependantsNet: row.formula.dependants_net,
        result: parseFloat(row.formula.result),
    },
    dependencyMultiplicand: row.dependencyMultiplicand,
    multiplier: row.multiplier,
    discountFactor: row.discountFactor,
    discountedMultiplier: row.discountedMultiplier,
    value: row.value,
});

// Initial State
const state = {
    total: null,
    discountTable: 28,
    periodSettlementToEnd: null,
    preAssessmentDiscount: null,
    postAssessmentDiscount: null,
    table28Multiplier: null,
    table1Or2Multiplier: null,
    table28: [],
    table1and2: [],
    durationOfDependency: null
};

/**
 * Validation Rules.
 * 
 * @type {Object}
 */
const rules = {
    //'table28' : { type: 'array', rules : 'required' },
    // 'discountTable' : { type: 'number', rules : 'required' },
    // 'durationOfDependency' : { type: 'number', rules : 'required' },
    // 'periodSettlementToEnd' : { type: 'number', rules : 'required' },
    // 'postAssessmentDiscount' : { type: 'number', rules : 'required' },
    // 'preAssessmentDiscount' : { type: 'number', rules : 'required' },
    // 'table1Or2Multiplier' : { type: 'number', rules : 'required' },
    // 'table28Multiplier' : { type: 'number', rules : 'required' },
    // 'postDependencyValue' : { type: 'number', rules : 'required|min:1' },
    // 'theDeceased.income.retirementAge' : { type: 'number', rules : 'required' },
    // 'theDeceased.income.retirementDate' : { type: 'date', rules : 'required' },
};

// Getters
const getters = {
    value: state => key => state[key],
    getAll: state => state,
    table28: state => state.table28,
    table1and2: state => state.table1and2,
    preDependencyValue: (state) => {
        if (state.discountTable === 28) {
            return state.table28
                .filter(row => row.period === 'pre')
                .reduce((total, row) => total + row.value, 0);
        }
        return state.table1and2
            .reduce((total, row) => total + row.pre.periods.reduce((total, period) => total + period.loss, 0), 0);
    },
    postDependencyValue: (state) => {
        if (state.discountTable === 28) {
            return state.table28
                .filter(row => row.period === 'post')
                .reduce((total, row) => total + row.value, 0);
        }
        return state.table1and2
            .reduce((total, row) => total + row.post.periods.reduce((total, period) => total + period.loss, 0), 0);
    },
    discountTable: state => state.discountTable,
    durationOfDependency: state => state.durationOfDependency
};

// Actions
const actions = {
    /**
     * Save the step by submitting to the server.
     *
     * @param dispatch
     * @param payload
     */
    save({ dispatch }, payload) {
        // We might change this to something which
        // is a bit cleaner...

        const durationFind = store.getters['fatal/dependency/dependants/durationOfDependency'];

        payload.data = {
            assumed_settlement: store.getters['fatal/dependency/multiplicand/total'],
            ...financeValues(store.getters['fatal/dependency/deceased/theDeceased'], store.getters['fatal/dependency/deceased/durationDeceasedDependency'], 'deceased_'),
            deceased: financeValues(store.getters['fatal/dependency/deceased/theDeceased'], store.getters['fatal/dependency/deceased/durationDeceasedDependency']),
            dependants: store.getters['fatal/dependency/dependants/dependants'].map((dependant, i) => financeValues(dependant, durationFind(i).duration)),
            multiplicand: store.getters['fatal/dependency/multiplicand/theMultiplicand'],
            discountTable: state.discountTable,
            table28: state.table28,
            table1and2: state.table1and2,
        };

        Api.claims.save(payload).then((response) => {
            return response;
        });

        // Now Update the first step, since that is where we store the discount rate
        store.dispatch('fatal/dependency/save', requestPayload(0));
    },

    /**
     * Loads the step being received from the Server, to be placed in the state.
     *
     * @param dispatch
     * @param commit
     * @param step
     */
    load({ dispatch, commit }, step) {
        const data = _.deeply(_.mapKeys)(step, (value, key) => _.camelCase(key)).data;

        commit(mutationTypes.LOAD_FROM_SERVER, data);
        store.dispatch('fatal/dependency/deceased/serverUpdateDeceasedIncome', {data});
        store.dispatch('fatal/dependency/dependants/serverUpdateDependantsIncome', {data});
     },

    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     */
    process({rootState}) {
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 3,
            valid: previousValid(rootState, 3) && (new Validator(state, rules)).validate(),
        });
    },

    /**
     * Validate the step.
     *
     * @param commit
     */
    validate({ rootState }) {
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 3,
            valid: previousValid(rootState, 3) && (new Validator(state, rules)).validate(), 
        }).then(() => {
            store.dispatch('fatal/dependency/updateTheMultiplicand', null, {root: true});
            store.dispatch('fatal/dependency/checkToUpdateMultipliers', null, { root: true });
            store.dispatch('fatal/dependency/dependants/updateDependencyOnIncomeValid', true);
        })
    },

    /**
     * Resets the step back to defaults
     *
     * @param commit
     */
    reset({ commit }) {        
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 3,
            valid: false,
        });

        commit(mutationTypes.RESET);
    },

    /**
     * Update Discount Rate
     *
     * @param commit
     * @param dispatch
     * @param rate
     */
    updateDiscountTable({ commit, dispatch }, rate) {
        commit(mutationTypes.UPDATE_DISCOUNT_TABLE, rate);
        return Promise.all([
            dispatch('fatal/dependency/checkToUpdateMultipliers', null, { root: true }),
            dispatch('fatal/dependency/updateServices', null, { root: true }),
            dispatch('fatal/dependency/updateServicesTable', null, { root: true }),
        ]);
    },

    /**
     * Update all income
     *
     * @param dispatch
     * @param state
     * @param data
     */
    updateAllIncome({dispatch, state}, data) {
        if (data.type === 'Deceased') {
            let updateIncome =  store.getters['fatal/dependency/deceased/theDeceased'].income.periods.map((period, index) => {
                if (!isValidIncome(period) || data.income.retirementAge < 18 || data.income.retirementAge === "" || data.income.retirementDate === null) {
                    return;
                }
                unthrottledIncome(period, dispatch, 'fatal/dependency/deceased/updateDeceasedIncomePeriodValue', {index}, store.getters['fatal/dependency/deceased/theDeceased']);
            });

            dispatch('fatal/dependency/updateTheMultiplicand', null, {root: true});
            return updateIncome;
        }
        let theDependant = store.getters['fatal/dependency/dependants/dependants'][data.index];
        data.income.periods.map((period, index) => {
            if (!isValidIncome(period)) {
                return;
            }
            unthrottledIncome(period, dispatch, 'fatal/dependency/dependants/updateDependantIncomePeriodValue', {index, dependant: theDependant, dependantIndex: data.index}, store.getters['fatal/dependency/deceased/theDeceased'], data.index);
        });

        dispatch('fatal/dependency/updateTheMultiplicand', null, {root: true});
    },

    /**
     * Update with responses from server
     *
     * @param commit
     * @param serverValues
     */
    updateFromServer({ commit }, serverValues) {
        commit(mutationTypes.UPDATE_MULTIPLIER_VALUE, {
            key: 'periodSettlementToEnd',
            value: serverValues.duration,
        });
        commit(mutationTypes.UPDATE_MULTIPLIER_VALUE, {
            key: 'preAssessmentDiscount',
            value: serverValues.pre_assessment_discount,
        });
        commit(mutationTypes.UPDATE_MULTIPLIER_VALUE, {
            key: 'postAssessmentDiscount',
            value: serverValues.post_assessment_discount,
        });
        commit(mutationTypes.UPDATE_MULTIPLIER_VALUE, {
            key: 'table28Multiplier',
            value: serverValues.table_28_multiplier,
        });
        commit(mutationTypes.UPDATE_MULTIPLIER_VALUE, {
            key: 'table1Or2Multiplier',
            value: serverValues.table_1_or_2_multiplier,
        });
        commit(mutationTypes.UPDATE_MULTIPLIER_VALUE, {
            key: 'table1Or2Multiplier',
            value: serverValues.duration,
        });
        commit(mutationTypes.UPDATE_MULTIPLIER_VALUE, {
            key: 'durationOfDependency',
            value: serverValues.duration,
        });
        commit(mutationTypes.UPDATE_MULTIPLIER_VALUE, { 
            key: 'value', 
            value: serverValues.value
        });
    },

    /**
     * Update Table 36 from server load
     *
     * @param commit
     * @param dispatch
     * @param serverValues
     */
    updateTable28FromClaimLoad({ commit, dispatch }, serverValues) {
        commit(mutationTypes.RESET_MULTIPLIER_TABLE);
        // this needs to be done better
        const objectLength = serverValues.length - 1;
        serverValues.forEach((row, key) => {
            commit(mutationTypes.UPDATE_MULTIPLIER_TABLE28_ROW, convertTableRowFromServer(row));
            if (key === objectLength) {
                dispatch('fatal/dependency/headsOfLoss/updatePastDependencyOnIncome', null, { root: true });
            }
        });
    },

    /**
     * Update table 1 and 2 from server load
     *
     * @param commit
     * @param dispatch
     * @param serverValues
     */
    updateTable1and2FromClaimLoad({ commit, dispatch }, serverValues) {
        commit(mutationTypes.RESET_MULTIPLIER_TABLE);
        commit(mutationTypes.LOAD_MULTIPLIER_TABLE1AND2, serverValues);
        dispatch('fatal/dependency/headsOfLoss/updatePastDependencyOnIncome', null, { root: true });
    },

    resetMultiplier({commit}) {
        commit(mutationTypes.RESET_MULTIPLIER_TABLE);
    }
};

// Mutations
const mutations = {
    [mutationTypes.UPDATE_MULTIPLIER_VALUE](state, { key, value }) {
        state[key] = value;
    },
    [mutationTypes.UPDATE_DISCOUNT_TABLE](state, table) {
        state.discountTable = parseInt(table, 10);
    },
    [mutationTypes.UPDATE_MULTIPLIER_TABLE28_ROW](state, row) {
        state.table28.push(row);
    },
    [mutationTypes.LOAD_MULTIPLIER_TABLE1AND2](state, serverValues) {
        state.table1and2.push(serverValues);
    },
    [mutationTypes.RESET_MULTIPLIER_TABLE](state) {
        state.table28 = [];
        state.table1and2 = [];
    },
    [mutationTypes.LOAD_FROM_SERVER](state, data) {
        state.discountTable = data.discountTable;
        state.periodSettlementToEnd = data.periodSettlementToEnd;
        state.preAssessmentDiscount = data.preAssessmentDiscount;
        state.postAssessmentDiscount = data.postAssessmentDiscount;
        state.table28Multiplier = data.table28Multiplier;
        state.table1Or2Multiplier = data.table1Or2Multiplier;
        state.value = data.value;
        state.table28 = data.table28;
        state.table1and2 = data.table1And2;
        state.durationOfDependency = data.durationOfDependency;
    },
    [mutationTypes.RESET](state) {
        state.discountTable = 28;
        state.periodSettlementToEnd = null;
        state.preAssessmentDiscount = null;
        state.postAssessmentDiscount = null;
        state.table28Multiplier = null;
        state.table1Or2Multiplier = null;
        state.value = null;
        state.table28 = [];
        state.table1and2 = [];
        state.durationOfDependency = null;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/fatal/dependency/dependencyOnIncome.js